import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as api from 'api/methods';

import { copyToClipboard } from 'helpers/copyToClipboard';
import { getTranslation } from 'helpers/getTranslation';
import { showToast } from 'helpers/showToast';

import { useSearch } from 'hooks/useSearch';

import {
  formatAccessFilterDateForApi,
  mapCuratorReportResponse,
  type CuratorReportData,
  type CuratorReportSortableColumn,
  type CuratorReportSortParams,
} from './SchoolCuratorReportPage.helpers';

import SchoolCuratorReportPage from './SchoolCuratorReportPage';

type SchoolCuratorReportPageContainerProps = {
  isPublic?: boolean;
};

import { DEFAULT_SCHOOL_CURATOR_REPORT_PER_PAGE } from './SchoolCuratorReportPage.pagination.config';

const DEFAULT_PER_PAGE = DEFAULT_SCHOOL_CURATOR_REPORT_PER_PAGE;

const SchoolCuratorReportPageContainer = ({
  isPublic: isPublicProp,
}: SchoolCuratorReportPageContainerProps) => {
  const { token, id: schoolId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const previousLang = i18n.language;
    void i18n.changeLanguage('ru');
    return () => {
      void i18n.changeLanguage(previousLang);
    };
  }, [i18n]);

  const isPublic = isPublicProp ?? Boolean(token);

  useEffect(() => {
    if (!isPublic) {
      return;
    }

    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#f8fafc';

    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, [isPublic]);

  useEffect(() => {
    if (searchParams.has('courseId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('courseId');
      setSearchParams(nextParams, { replace: true });
    }
    // Strip legacy courseId query param on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCopyingShareLink, setIsCopyingShareLink] = useState(false);
  const [reportData, setReportData] = useState<CuratorReportData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [searchQuery, onSearchChange] = useSearch() as [
    string,
    (event: React.ChangeEvent<HTMLInputElement>) => void,
  ];
  const [accessFrom, setAccessFrom] = useState('');
  const [accessTo, setAccessTo] = useState('');
  const [sortParams, setSortParams] = useState<CuratorReportSortParams>({
    sortBy: 'access',
    sortDirection: 'desc',
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, accessFrom, accessTo]);

  useEffect(() => {
    if (isPublic && !token) {
      return;
    }

    if (!isPublic && !schoolId) {
      return;
    }

    let isCancelled = false;

    const loadReport = async () => {
      setLoading(true);

      try {
        const apiAccessFrom = formatAccessFilterDateForApi(accessFrom);
        const apiAccessTo = formatAccessFilterDateForApi(accessTo);

        const requestParams = {
          page: currentPage,
          limit: perPage,
          ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
          ...(apiAccessFrom ? { accessFrom: apiAccessFrom } : {}),
          ...(apiAccessTo ? { accessTo: apiAccessTo } : {}),
          ...(sortParams.sortBy
            ? {
                sortBy: sortParams.sortBy,
                sortDirection: sortParams.sortDirection,
              }
            : {}),
        };

        const response = isPublic
          ? await api.getPublicCuratorReport(token as string, requestParams)
          : await api.getCuratorReport(schoolId as string, requestParams);

        if (!isCancelled) {
          const mappedReport = mapCuratorReportResponse(
            response as Parameters<typeof mapCuratorReportResponse>[0],
          );

          if (
            mappedReport.pagination &&
            currentPage > mappedReport.pagination.totalPages &&
            mappedReport.pagination.totalPages > 0
          ) {
            setCurrentPage(mappedReport.pagination.totalPages);
          } else {
            setReportData(mappedReport);
          }
        }
      } catch (error) {
        console.warn(error);

        if (!isCancelled) {
          setReportData(null);
        }
      }

      if (!isCancelled) {
        setLoading(false);
      }
    };

    loadReport();

    return () => {
      isCancelled = true;
    };
  }, [
    isPublic,
    schoolId,
    token,
    currentPage,
    perPage,
    searchQuery,
    accessFrom,
    accessTo,
    sortParams.sortBy,
    sortParams.sortDirection,
  ]);

  const handleCopyShareLink = async () => {
    if (!schoolId) {
      return;
    }

    setIsCopyingShareLink(true);

    try {
      const response = (await api.createCuratorReportShareLink(
        schoolId,
      )) as { token?: string; url?: string };

      const shareUrl =
        response?.url ||
        `${window.location.origin}/school-report/${response?.token}`;

      await copyToClipboard(shareUrl);

      showToast({
        data: {
          variant: 'success',
          title: getTranslation(
            'school_curator_report_page.share_link_copied.title',
          ),
          text: getTranslation(
            'school_curator_report_page.share_link_copied.text',
          ),
        },
      });
    } catch (error) {
      console.warn(error);

      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('common.toast.error.text'),
        },
      });
    }

    setIsCopyingShareLink(false);
  };

  const handleRefreshReport = async () => {
    if (!schoolId && !token) {
      return;
    }

    setIsRefreshing(true);

    try {
      const apiAccessFrom = formatAccessFilterDateForApi(accessFrom);
      const apiAccessTo = formatAccessFilterDateForApi(accessTo);

      const requestParams = {
        page: currentPage,
        limit: perPage,
        ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
        ...(apiAccessFrom ? { accessFrom: apiAccessFrom } : {}),
        ...(apiAccessTo ? { accessTo: apiAccessTo } : {}),
        ...(sortParams.sortBy
          ? {
              sortBy: sortParams.sortBy,
              sortDirection: sortParams.sortDirection,
            }
          : {}),
      };

      const response =
        isPublic && token
          ? await api.refreshPublicCuratorReport(token, requestParams)
          : await api.refreshCuratorReport(schoolId as string, requestParams);

      const mappedReport = mapCuratorReportResponse(
        response as Parameters<typeof mapCuratorReportResponse>[0],
      );

      if (
        mappedReport.pagination &&
        currentPage > mappedReport.pagination.totalPages &&
        mappedReport.pagination.totalPages > 0
      ) {
        setCurrentPage(mappedReport.pagination.totalPages);
      } else {
        setReportData(mappedReport);
      }
    } catch (error) {
      console.warn(error);

      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('common.toast.error.text'),
        },
      });
    }

    setIsRefreshing(false);
  };

  const handleStudentClick = useCallback(
    (studentId: string, primaryCourseId: string) => {
      if (isPublic && token) {
        navigate(
          `/school-report/${token}/students/${studentId}?courseId=${primaryCourseId}`,
        );
        return;
      }

      if (schoolId) {
        navigate(
          `/schools/${schoolId}/curator-dashboard/students/${studentId}?courseId=${primaryCourseId}`,
        );
      }
    },
    [isPublic, navigate, schoolId, token],
  );

  const handlePerPageChange = useCallback((nextPerPage: number) => {
    setPerPage(nextPerPage);
    setCurrentPage(1);
  }, []);

  const handleNextPageClick = useCallback(() => {
    setCurrentPage((page) =>
      Math.min(page + 1, reportData?.pagination?.totalPages ?? page),
    );
  }, [reportData?.pagination?.totalPages]);

  const handlePrevPageClick = useCallback(() => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  }, []);

  const handleSortChange = useCallback((nextSortBy: string) => {
    const sortColumn = nextSortBy as CuratorReportSortableColumn;

    setSortParams((previousSortParams) => {
      if (previousSortParams.sortBy === sortColumn) {
        return {
          sortBy: sortColumn,
          sortDirection:
            previousSortParams.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        sortBy: sortColumn,
        sortDirection: 'desc',
      };
    });
    setCurrentPage(1);
  }, []);

  return (
    <SchoolCuratorReportPage
      locale="ru"
      isPublic={isPublic}
      loading={loading}
      reportData={reportData}
      students={reportData?.students ?? []}
      pagination={reportData?.pagination}
      searchQuery={searchQuery}
      accessFrom={accessFrom}
      accessTo={accessTo}
      onAccessFromChange={setAccessFrom}
      onAccessToChange={setAccessTo}
      currentPage={currentPage}
      perPage={perPage}
      onSearchChange={onSearchChange}
      onPerPageChange={handlePerPageChange}
      onNextPageClick={handleNextPageClick}
      onPrevPageClick={handlePrevPageClick}
      onCopyShareLink={!isPublic ? handleCopyShareLink : undefined}
      isCopyingShareLink={isCopyingShareLink}
      onRefreshReport={handleRefreshReport}
      isRefreshing={isRefreshing}
      onStudentClick={handleStudentClick}
      sortParams={sortParams}
      onSortChange={handleSortChange}
    />
  );
};

export default React.memo(SchoolCuratorReportPageContainer) as any;
